import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import TopNavbar from '../top-navbar/TopNav';
import '../../styles/employee/add-attachment.css';
import { toast } from 'react-toastify';
import { FaTimes } from 'react-icons/fa';
import { getAttachments, saveVirtualEmployment } from '../../services/employee/api';

const Attachment = ({ candidateId, employmentInfos = [], onBack }) => {
  const [files, setFiles] = useState([]);
  const [uploadedUrls, setUploadedUrls] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileErrors, setFileErrors] = useState(Array(employmentInfos.length).fill(""));

  // Fetch existing attachments on page load
  const fetchAttachments = async () => {
    try {
      const response = await getAttachments(candidateId);
      const data = Array.isArray(response.data) ? response.data : [];

      const initialFiles = Array(employmentInfos.length).fill(null);
      const initialUrls = Array(employmentInfos.length).fill(null);

      employmentInfos.forEach((info, index) => {
        const existing = data.find(
          (att) => att.employeeCode === info.employeeCode
        );
        if (existing) {
          initialUrls[index] = existing.filename || existing.employmentFileUrl || null;
          if (existing.remarks) setRemarks(existing.remarks);
          if (existing.isVerified === "COMPLETED" || existing.individualStatus === "COMPLETED") {
            setIsVerified(true);
          }
        }
      });

      setFiles(initialFiles);
      setUploadedUrls(initialUrls);
    } catch (err) {
      console.warn("Could not pre-load attachments:", err);
    }
  };

  useEffect(() => {
    if (candidateId) {
      fetchAttachments();
    }
  }, [candidateId]);

  // Handle new file selection
  const handleFileChange = (e, index) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
    const maxSize = 5 * 1024 * 1024;

    const updatedErrors = [...fileErrors];

    if (!allowedTypes.includes(file.type)) {
      updatedErrors[index] = "Only PDF, JPG, PNG files are allowed.";
      setFileErrors(updatedErrors);
      return;
    }

    if (file.size > maxSize) {
      updatedErrors[index] = "File size must be less than 5MB.";
      setFileErrors(updatedErrors);
      return;
    }

    updatedErrors[index] = "";
    setFileErrors(updatedErrors);

    const updatedFiles = [...files];
    updatedFiles[index] = file;
    setFiles(updatedFiles);

    const updatedUrls = [...uploadedUrls];
    updatedUrls[index] = URL.createObjectURL(file);
    setUploadedUrls(updatedUrls);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
    const maxSize = 5 * 1024 * 1024;

    const updatedErrors = [...fileErrors];

    if (!allowedTypes.includes(file.type)) {
      updatedErrors[index] = "Only PDF, JPG, PNG files are allowed.";
      setFileErrors(updatedErrors);
      return;
    }

    if (file.size > maxSize) {
      updatedErrors[index] = "File size must be less than 5MB.";
      setFileErrors(updatedErrors);
      return;
    }

    updatedErrors[index] = "";
    setFileErrors(updatedErrors);

    const updatedFiles = [...files];
    updatedFiles[index] = file;
    setFiles(updatedFiles);

    const updatedUrls = [...uploadedUrls];
    updatedUrls[index] = URL.createObjectURL(file);
    setUploadedUrls(updatedUrls);
  };

  const removeAttachmentFile = (index) => {
    const updatedFiles = [...files];
    updatedFiles[index] = null;
    setFiles(updatedFiles);

    const updatedUrls = [...uploadedUrls];
    updatedUrls[index] = null;
    setUploadedUrls(updatedUrls);
  };

  // Validation
  const validateForm = () => {
    const hasAnyFile = files.some((f) => f !== null) || uploadedUrls.some((url) => url !== null);
    if (!hasAnyFile) {
      toast.error('At least one proof must be uploaded.');
      return false;
    }

    if (!remarks.trim()) {
      toast.error('Remarks are mandatory.');
      return false;
    }
    return true;
  };

  // Submit Handler -> Calls saveVirtualEmployment for each employment entry
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Loop over each employment info and post to /employee/verify/virtual
      for (let index = 0; index < employmentInfos.length; index++) {
        const info = employmentInfos[index];
        const file = files[index];

        const formData = new FormData();
        formData.append("candidateId", candidateId);
        formData.append("employeeCode", info.employeeCode || info.empCode || "");
        formData.append("companyName", info.companyName || info.company || "");
        formData.append("jobTitle", info.jobTitle || info.designation || "N/A");
        formData.append("location", info.location || info.city || "N/A");
        formData.append("workingFrom", info.workingFrom || info.durationFrom || info.fromDate || "N/A");
        formData.append("workingTo", info.workingTo || info.durationTo || info.toDate || "N/A");
        formData.append("hrContactNumber", info.hrContactNumber || info.employerMobileNumber || "");
        formData.append("hrContactEmail", info.hrContactEmail || info.email || "");

        // Virtual Verification status and remarks
        formData.append("remarks", remarks);
        formData.append("verificationStatus", isVerified ? "COMPLETED" : "PENDING");

        if (file) {
          formData.append("proofFile", file);
        }

        await saveVirtualEmployment(formData);
      }

      toast.success("Virtual employment verification submitted successfully!");
      onBack();
    } catch (err) {
      console.error('Error submitting virtual verification:', err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to submit virtual employment verification.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="dashboard-app-container">
      <Sidebar />
      <div className="dashboard-right-panel">
        <TopNavbar />
        <header className="attachment-header-bar">
          <h2 className="attachment-header-title">Add Verification Proof</h2>
        </header>
        <div className="attachment-container">
          <div className="attachment-form-container">
            <form onSubmit={handleSubmit}>
              <div className="attachment-file-uploads-section">
                {employmentInfos.map((info, index) => (
                  <div key={info.id || index} className="attachment-file-upload-card">
                    <h3 className="attachment-upload-section-title">
                      {info.companyName} Proof
                    </h3>
                    <div className="attachment-file-input-group">
                      <label
                        className="attachment-choose-file-button"
                        htmlFor={`attachment-file-input-${index}`}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index)}
                      >
                        Drag & Drop or Click to Upload
                      </label>
                      <input
                        id={`attachment-file-input-${index}`}
                        type="file"
                        accept="image/jpeg,image/png,application/pdf"
                        onChange={(e) => handleFileChange(e, index)}
                        className="attachment-hidden-file-input"
                      />

                      {(files[index] || uploadedUrls[index]) && (
                        <div className="file-list">
                          <span className="file-list-item">
                            {files[index]
                              ? files[index].name
                              : (uploadedUrls[index] || '').split('/').pop()}
                            <FaTimes
                              className="remove-file-icon"
                              onClick={() => removeAttachmentFile(index)}
                            />
                          </span>
                        </div>
                      )}
                    </div>
                    <small className="attachment-file-size-info">
                      * File must be PDF / JPG / PNG and less than 5MB
                    </small>

                    {fileErrors[index] && (
                      <p className="file-error">{fileErrors[index]}</p>
                    )}
                  </div>
                ))}
              </div>

              <div className="attachment-common-details-card">
                <label className="attachment-remarks-label">Remarks*</label>
                <textarea
                  className="attachment-remarks-textarea"
                  placeholder="Type verification notes here..."
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                ></textarea>

                <div className="attachment-verification-status-group">
                  <label className="attachment-checkbox-label">
                    <input
                      type="checkbox"
                      checked={isVerified}
                      onChange={(e) => setIsVerified(e.target.checked)}
                      className="attachment-verification-checkbox"
                    />
                    Verification Status (Verified)
                  </label>
                  <p className="verification-note">
                    ⚠️ Once verified, this step will be locked and saved into the final report.
                  </p>
                </div>
              </div>

              <div className="attachment-button-group">
                <button
                  type="button"
                  className="attachment-cancel-button"
                  onClick={onBack}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="attachment-submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Attachment;